import { BsPencilSquare } from '@icon/react-icons';
import { useBoolean, useSnackbarInstance } from '@lib/common';
import { AvatarProps, Avatar, IconButton, Grid, Tooltip } from '@material-ui/core';
import React, { useCallback } from 'react';
import { useState, useEffect } from 'react';
import { ImageEditor } from './image-editor-dialog';
import { OverlayContainer, Overlay } from './overlay';
import PublishIcon from '@material-ui/icons/Publish';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import { red } from '@material-ui/core/colors';

export interface EditableAvatarProps extends Omit<AvatarProps, 'src' | 'srcList' | 'onChange'> {
  height?: React.CSSProperties['height'];
  width?: React.CSSProperties['width'];
  value?: string;
  empty?: React.ReactNode;
  onChange?: (value: string) => any;
  onDelete?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  hideDelete?: boolean;
  hideEdit?: boolean;
  hideUpload?: boolean;
  hideDownload?: boolean;
}

export const EditableAvatar = ({
  height = 128,
  width = 128,
  value: src = null,
  empty = 'None',
  onChange = null,
  onDelete = null,
  hideDelete = false,
  hideEdit = false,
  hideUpload = false,
  hideDownload = false,
  ...props
}: EditableAvatarProps) => {
  const [showEditor, openEditor, closeEditor] = useBoolean(false);
  const [image, setImage] = useState<File | string>(null);
  const [value, setValue] = useState(src);
  const { enqueue } = useSnackbarInstance();

  useEffect(() => {
    setValue(src);
  }, [src]);

  const handleFileChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const file = evt.target.files[0];
      if (!file) return;
      if (file.size > 5000000) {
        enqueue('Image size cannot exceed 5000kb', { variant: 'error' });
        return;
      }
      // clear files
      evt.target.value = '';
      setImage(file);
      openEditor();
    },
    [openEditor, setImage, enqueue]
  );

  const onEditClick = () => {
    setImage(src);
    openEditor();
  };

  const onDeleteClick: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement> = evt => {
    setImage(null);
    setValue(null);
    onChange && onChange(null);
    onDelete && onDelete(evt);
  };

  const onEditorDone = useCallback(
    ({ imageCanvas }) => {
      const dataUrl = imageCanvas.toDataURL();
      setValue(dataUrl);
      onChange && onChange(dataUrl);
    },
    [setValue, onChange]
  );

  return (
    <>
      <OverlayContainer>
        <Avatar
          imgProps={containImageStyle}
          src={value || null}
          style={{ ...props?.style, height, width }}
          {...props}>
          {empty}
        </Avatar>
        <Overlay>
          <div
            style={{
              display: 'flex',
              background: 'rgba(21,21,21,.6)',
              justifyContent: 'center',
              alignItems: 'center',
              height,
              width,
              borderRadius: '50%',
              userSelect: 'none',
            }}>
            <Grid container spacing={1} justify="center" alignItems="center">
              {!hideEdit && (
                <Grid item>
                  <Tooltip title="Edit the image">
                    <IconButton style={{ color: 'white' }} onClick={onEditClick}>
                      <BsPencilSquare />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              {!hideDelete && (
                <Grid item>
                  <Tooltip title="Delete the image">
                    <IconButton style={{ color: 'white' }} onClick={onDeleteClick}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              {!hideUpload && (
                <Grid item>
                  <Tooltip title="Upload new image">
                    <span>
                      <IconButton style={{ color: 'white' }}>
                        <input
                          onChange={handleFileChange}
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="contained-button-file"
                          type="file"
                        />
                        <label htmlFor="contained-button-file" style={{ cursor: 'pointer' }}>
                          <PublishIcon />
                        </label>
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              )}
              {!hideDelete && (
                <Grid item>
                  <Tooltip title="Download the image">
                    <IconButton style={{ color: 'white' }} component="a" href={value} download>
                      <GetAppIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          </div>
          {/* <label htmlFor="contained-button-file">
            <Avatar style={{ height, width, background: 'rgba(21,21,21,.6)' }}>
            <BsPencilSquare />
            </Avatar>
          </label> */}
        </Overlay>
      </OverlayContainer>
      {showEditor && image && (
        <ImageEditor
          image={image}
          borderRadius={100}
          open={showEditor}
          onClose={closeEditor}
          onFinish={onEditorDone}
        />
      )}
    </>
  );
};

const containImageStyle = { style: { objectFit: 'contain' } } as const;
